import React, { useState } from "react";
import { TspFormField, TspModal } from "../../../components/primitives";
import { FORUM_TYPES, TOPIC_BODY_MAX, TOPIC_FIELD_ORDER, TOPIC_TITLE_MAX, validateTopic } from "../forumModel";

// "Start a topic" dialog. Mounted only while open, so its state resets each time.
export default function TopicFormModal({ categories, defaultCategory = "", onClose, onSubmit }) {
  const [values, setValues] = useState({ title: "", category: defaultCategory, type: "DISCUSSION", body: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const setField = (key, value) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const found = validateTopic(values, categories);
    setErrors(found);
    const firstInvalid = TOPIC_FIELD_ORDER.find((key) => found[key]);
    if (firstInvalid) {
      document.getElementById(`tsp-topic-${firstInvalid}`)?.focus();
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TspModal
      open
      title="Start a New Topic"
      onClose={submitting ? undefined : onClose}
      footer={
        <>
          <button type="button" className="tsp-btn tsp-btn-outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" form="tsp-topic-form" className="tsp-btn tsp-btn-success" disabled={submitting}>
            {submitting ? "Posting…" : "Post Topic"}
          </button>
        </>
      }
    >
      <form id="tsp-topic-form" onSubmit={handleSubmit} noValidate className="tsp-topic-form">
        <TspFormField id="tsp-topic-title" label="Title" required error={errors.title} hint={`${values.title.length}/${TOPIC_TITLE_MAX}`}>
          <input
            id="tsp-topic-title"
            type="text"
            className="tsp-field-control"
            value={values.title}
            maxLength={TOPIC_TITLE_MAX + 20}
            autoComplete="off"
            aria-invalid={errors.title ? "true" : undefined}
            aria-describedby={errors.title ? "tsp-topic-title-error" : "tsp-topic-title-hint"}
            onChange={(event) => setField("title", event.target.value)}
          />
        </TspFormField>

        <div className="tsp-journey-form-grid">
          <TspFormField id="tsp-topic-category" label="Category" required error={errors.category}>
            <select
              id="tsp-topic-category"
              className="tsp-field-control"
              value={values.category}
              aria-invalid={errors.category ? "true" : undefined}
              aria-describedby={errors.category ? "tsp-topic-category-error" : undefined}
              onChange={(event) => setField("category", event.target.value)}
            >
              <option value="">Select category</option>
              {categories.map((category) => (
                <option key={category.key} value={category.key}>
                  {category.label}
                </option>
              ))}
            </select>
          </TspFormField>

          <TspFormField id="tsp-topic-type" label="This is a" required error={errors.type}>
            <select
              id="tsp-topic-type"
              className="tsp-field-control"
              value={values.type}
              aria-invalid={errors.type ? "true" : undefined}
              aria-describedby={errors.type ? "tsp-topic-type-error" : undefined}
              onChange={(event) => setField("type", event.target.value)}
            >
              {FORUM_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </TspFormField>
        </div>

        <TspFormField id="tsp-topic-body" label="Details" required error={errors.body} hint={`${values.body.length}/${TOPIC_BODY_MAX}`}>
          <textarea
            id="tsp-topic-body"
            className="tsp-field-control"
            rows={7}
            value={values.body}
            aria-invalid={errors.body ? "true" : undefined}
            aria-describedby={errors.body ? "tsp-topic-body-error" : "tsp-topic-body-hint"}
            onChange={(event) => setField("body", event.target.value)}
          />
        </TspFormField>
      </form>
    </TspModal>
  );
}
